// First-touch ad attribution persistence.
//
// The problem this solves: ad click ids (msclkid/gclid) and utm_* only ride the *landing*
// URL. By the time a visitor converts deep in the builder (/builder/), window.location.search
// is empty, so reading attribution at conversion time always yielded "". Every conversion
// POST to the D1 worker therefore stored a null msclkid, and the Microsoft offline-conversion
// importer (which keys on msclkid) had nothing to attribute — Microsoft Ads showed ~0 conv.
//
// Fix: capture attribution from the URL on first landing into localStorage, then replay it on
// every conversion POST. Mirrors what PostHog does internally with its $initial_* person props.

const KEY = "rd_attribution";

const FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_adgroup",
  "gclid",
  "msclkid",
] as const;

type Field = (typeof FIELDS)[number];
export type Attribution = Partial<Record<Field, string>> & { landing_url?: string };

function readStored(): Attribution {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(KEY) || "{}") as Attribution;
  } catch {
    return {};
  }
}

/** Run once on each page load. Persists any attribution params present in the current URL.
 *  Non-empty values overwrite (captures the click id that brought them); empty values never
 *  clear a stored value, so later internal/organic navigations don't wipe the original click. */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    const search = new URLSearchParams(window.location.search);
    const stored = readStored();
    let changed = false;
    for (const f of FIELDS) {
      const v = search.get(f);
      if (v && stored[f] !== v) {
        stored[f] = v;
        changed = true;
      }
    }
    // Record the true first-touch landing URL once, when a click id / source first appears.
    if (!stored.landing_url && (search.get("msclkid") || search.get("gclid") || search.get("utm_source"))) {
      stored.landing_url = window.location.href.substring(0, 500);
      changed = true;
    }
    if (changed) window.localStorage.setItem(KEY, JSON.stringify(stored));
  } catch {
    /* private mode / quota — getAttribution() falls back to the live URL */
  }
}

/** Attribution for a conversion POST: persisted first-touch values, falling back to the
 *  current URL for anything not stored. Always returns every field (empty string if unknown). */
export function getAttribution(): Required<Pick<Attribution, Field>> & { landing_url: string } {
  const stored = readStored();
  const search = typeof window === "undefined" ? new URLSearchParams() : new URLSearchParams(window.location.search);
  const out = {} as Required<Pick<Attribution, Field>> & { landing_url: string };
  for (const f of FIELDS) out[f] = stored[f] || search.get(f) || "";
  out.landing_url =
    stored.landing_url || (typeof window === "undefined" ? "" : window.location.href.substring(0, 500));
  return out;
}
